/**
 * Tiny safe arithmetic evaluator for calculated-metric widgets.
 *
 * Why a custom evaluator instead of `Function` or `eval`?
 *   - Dashboard formulas are saved client-side and rendered server-side
 *     (in scheduled reports). Allowing arbitrary JS would be RCE.
 *   - Keep the surface area small: numbers, +, -, *, /, parentheses,
 *     unary minus, and {field} variable refs. Nothing else.
 *
 * Returns NaN when fields are missing or the expression is malformed —
 * the renderer shows that as `—` so a typo doesn't crash the dashboard.
 */

type Token =
  | { kind: 'num'; value: number }
  | { kind: 'var'; name: string }
  | { kind: 'op'; value: '+' | '-' | '*' | '/' }
  | { kind: 'lparen' }
  | { kind: 'rparen' };

function tokenize(expr: string): Token[] | null {
  const tokens: Token[] = [];
  let i = 0;
  while (i < expr.length) {
    const c = expr[i]!;
    if (c === ' ' || c === '\t' || c === '\n') {
      i++;
      continue;
    }
    if (c === '+' || c === '-' || c === '*' || c === '/') {
      tokens.push({ kind: 'op', value: c });
      i++;
      continue;
    }
    if (c === '(') {
      tokens.push({ kind: 'lparen' });
      i++;
      continue;
    }
    if (c === ')') {
      tokens.push({ kind: 'rparen' });
      i++;
      continue;
    }
    if (c === '{') {
      const end = expr.indexOf('}', i + 1);
      if (end === -1) return null;
      const name = expr.slice(i + 1, end).trim();
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) return null;
      tokens.push({ kind: 'var', name });
      i = end + 1;
      continue;
    }
    if ((c >= '0' && c <= '9') || c === '.') {
      let j = i + 1;
      while (j < expr.length && (/[\d.]/.test(expr[j]!) || expr[j] === 'e' || expr[j] === 'E')) j++;
      const n = Number(expr.slice(i, j));
      if (!Number.isFinite(n)) return null;
      tokens.push({ kind: 'num', value: n });
      i = j;
      continue;
    }
    return null; // unrecognised character
  }
  return tokens;
}

interface ParserState {
  tokens: Token[];
  pos: number;
}

function peek(p: ParserState): Token | null {
  return p.tokens[p.pos] ?? null;
}

function eat(p: ParserState): Token | null {
  return p.tokens[p.pos++] ?? null;
}

function parsePrimary(p: ParserState, vars: Record<string, number>): number {
  const t = eat(p);
  if (!t) return Number.NaN;
  if (t.kind === 'num') return t.value;
  if (t.kind === 'var') {
    const v = vars[t.name];
    return typeof v === 'number' && Number.isFinite(v) ? v : Number.NaN;
  }
  if (t.kind === 'lparen') {
    const v = parseExpr(p, vars);
    const r = eat(p);
    if (!r || r.kind !== 'rparen') return Number.NaN;
    return v;
  }
  if (t.kind === 'op' && t.value === '-') {
    return -parsePrimary(p, vars);
  }
  if (t.kind === 'op' && t.value === '+') {
    return parsePrimary(p, vars);
  }
  return Number.NaN;
}

function parseTerm(p: ParserState, vars: Record<string, number>): number {
  let lhs = parsePrimary(p, vars);
  while (true) {
    const t = peek(p);
    if (!t || t.kind !== 'op' || (t.value !== '*' && t.value !== '/')) break;
    eat(p);
    const rhs = parsePrimary(p, vars);
    if (t.value === '*') lhs = lhs * rhs;
    else lhs = rhs === 0 ? Number.NaN : lhs / rhs;
  }
  return lhs;
}

function parseExpr(p: ParserState, vars: Record<string, number>): number {
  let lhs = parseTerm(p, vars);
  while (true) {
    const t = peek(p);
    if (!t || t.kind !== 'op' || (t.value !== '+' && t.value !== '-')) break;
    eat(p);
    const rhs = parseTerm(p, vars);
    if (t.value === '+') lhs = lhs + rhs;
    else lhs = lhs - rhs;
  }
  return lhs;
}

export function evaluateFormula(expr: string, vars: Record<string, number>): number {
  if (!expr || !expr.trim()) return Number.NaN;
  const tokens = tokenize(expr);
  if (!tokens) return Number.NaN;
  const state: ParserState = { tokens, pos: 0 };
  const value = parseExpr(state, vars);
  if (state.pos !== state.tokens.length) return Number.NaN; // trailing junk
  return value;
}

/** Extract the {field} names referenced by a formula — used by the builder UI for autocomplete and validation. */
export function extractFormulaVariables(expr: string): string[] {
  const out = new Set<string>();
  for (const m of expr.matchAll(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g)) {
    out.add(m[1]!);
  }
  return [...out];
}
